// Import for Datapicker

import { registerLocale } from "react-datepicker";
import * as localefns from "date-fns/locale";

import { DEFAULT_LANGUAGE, USER_PROFILE_TYPES } from "../constants";
import { TenantStorage } from "./tenant-storage";
import moment from "moment";
import "moment/min/locales";

import store from "../redux/store";
import _frontEndDictionaryJSON from "../translations/en-US.json";
import { PhaseTypeName, ProcessPhase } from "../types";
import { getFeatureConfiguration } from "./feature-configuration-helper";
import { isEmpty, isNull } from "lodash";
import { _setLiteralsConfiguration } from "../redux/slices/literalsConfigurationSlice";
import * as Api from "../../src/api";
const _frontEndDictionary = _frontEndDictionaryJSON as Record<string, string>;

// Custom Locale object
export interface LocaleObject {
  locale: string;
  messages: any;
}

// Default languages supported by the app - check isoCode exits in date-fns/locale
export const APP_LANGUAGES = [
  //global / parent (discontinued property and inheritance )
  { isoCode: "en-US", name: "English (US)" },
  { isoCode: "es-ES", name: "Español (ES)" },
  { isoCode: "ro-RO", name: "Română" },
  { isoCode: "tr-TR", name: "Türkçe" },
  { isoCode: "cs-CZ", name: "čeština" },
  { isoCode: "fr-FR", name: "Français (FR)" },
  { isoCode: "de-DE", name: "Deutsch (DE)" },
  { isoCode: "nl-NL", name: "Nederlands" },
  { isoCode: "it-IT", name: "Italiano (IT)" },
  { isoCode: "pl-PL", name: "Polski" },
  //zh-Hant is the standard in ISO 15924, but not used for compatibility issues
  { isoCode: "zh-CN", name: "汉语" }, // Chinese simplified;
  { isoCode: "zh-TW", name: "繁體中文" }, //Chinese traditional;

  { isoCode: "ja-JP", name: "日本語" },
  { isoCode: "pt-PT", name: "Português (PT)" },
  { isoCode: "ca-ES", name: "Catalan (ES)" },
  { isoCode: "ms-MY", name: "Bahasa Melayu" },

  { isoCode: "uk-UA", name: "українська" },

  //regional (deprecated property)
  { isoCode: "en-AU", name: "English (AU)" },
  { isoCode: "en-CA", name: "English (CA)" },
  { isoCode: "en-GB", name: "English (UK)" },
  { isoCode: "en-IN", name: "English (IN)" },
  { isoCode: "en-JP", name: "English (JP)" },
  { isoCode: "es-LT", name: "Español (LATAM)" },
  { isoCode: "es-EC", name: "Español (EC)" },
  { isoCode: "es-CO", name: "Español (CO)" },
  { isoCode: "es-MX", name: "Español (MX)" },
  { isoCode: "es-AR", name: "Español (AR)" },
  { isoCode: "es-UY", name: "Español (UY)" },
  { isoCode: "es-US", name: "Español (US)" },
  { isoCode: "fr-CH", name: "Français (CH)" },
  { isoCode: "fr-CA", name: "Français (CA)" },
  { isoCode: "fr-LU", name: "Français (LU)" },
  { isoCode: "fr-BE", name: "Français (BE)" },
  { isoCode: "nl-BE", name: "Vlaams (BE)" },
  { isoCode: "de-CH", name: "Deutsch (CH)" },
  { isoCode: "it-CH", name: "Italiano (CH)" },
] as const;

type AppLanguage = (typeof APP_LANGUAGES)[number]["isoCode"];

export interface LanguageCodesObject {
  isoCode: AppLanguage;
  name: string;
}
export interface LiteralObj {
  id: string | undefined;
}

const removeHyphens = (language: string) => {
  return language.replace("-", "").replace("_", "").trim() || DEFAULT_LANGUAGE;
};

export const getUserProfileName = (role: string) => {
  const finalRole = role === USER_PROFILE_TYPES.user ? USER_PROFILE_TYPES.fullDeskRecruiter : capitalize(role?.replace("user", "").replace(/-/g, "\n").trim());
  return finalRole;
};

export const iso2ToLongLanguage = (language: string) => {
  // languages not in the global or the nearest iso2-code
  return DEFAULT_LOCALES.includes(language) ? language : DEFAULT_LOCALES.find((languageName: string) => language === languageName?.split("-")[0]) || DEFAULT_LANGUAGE;
};

const tenantStorage = new TenantStorage();
const internalLocalefns = { ...localefns } as any;
export const DEFAULT_LOCALES: string[] = APP_LANGUAGES.map((lang) => lang.isoCode);
// Browser locale
export const BROWSER_LANGUAGE = window.location.href.indexOf("sourcing/") < 0 ? navigator.language : tenantStorage.getLanguage();
// Current App locale used ONLY for API-calls
export let CURRENT_LOCALE = BROWSER_LANGUAGE?.split(/[-_]/)[0];
document.documentElement.setAttribute("lang", CURRENT_LOCALE);
const languageNames = DEFAULT_LOCALES.map((language: any) => language?.split(/[-_]/)[0]);
document.documentElement.setAttribute("translate", languageNames.includes(CURRENT_LOCALE) ? "no" : "yes");

// Map of currently supported languages
const localeMessages: any = {};
DEFAULT_LOCALES.forEach((locale: string) => {
  localeMessages[locale] = {
    locale: iso2ToLongLanguage(locale),
  };
});

const originalToLocaleDateString = Date.prototype.toLocaleDateString;

// Define your custom locale mapping

// Override the toLocaleDateString
Date.prototype.toLocaleDateString = function (this: Date, locales?: string | string[], options?: Intl.DateTimeFormatOptions): string {
  // Handle the case where locales is a single string
  if (typeof locales === "string") {
    return originalToLocaleDateString.call(this, locales, options);
  }
  // Default behavior for other cases
  return originalToLocaleDateString.call(this, locales, options);
};

export function getLocaleDate(locale: string) {
  const iso2Locale = locale?.split("-")[0];
  let localeLong = removeHyphens(locale);
  registerLocale(iso2Locale, internalLocalefns[localeLong] || internalLocalefns[iso2Locale] || localefns.enUS);
  return iso2Locale;
}

/**
 * Method that sets the {@link CURRENT_LOCALE} variable for the API-calls
 * @param locale is a {@link string} with the locale to be set
 */
export function setCurrentLocale(locale: string) {
  CURRENT_LOCALE = locale;
  tenantStorage.setLanguage(locale);
}

/**
 * Method that returns a {@link string} with the text to be displayed
 * on the language dropdown
 *
 * @param lang a {@link string} containing the locale
 * @returns a {@link string} with the language translated
 */
export function getLanguageString(lang: string): string {
  const _languageString = APP_LANGUAGES?.find((language) => language.isoCode === lang);
  return _languageString?.name || "unknown language";
}

/**
 * Function that returns an image corresponding to the given locale
 *
 * @param {string} localeString is a string with the locale
 */
export function getLocaleImage(localeString: string) {
  const longLanguage = iso2ToLongLanguage(localeString);
  try {
    if (DEFAULT_LOCALES.includes(longLanguage)) {
      return require(`../assets/images/common/flags/flag-${longLanguage}.png`)?.default;
    }
  } catch (error) {
    console.error(error);
  }
  return "";
}

/**
 * Method that returns a localized date for a given {@link number} timestamp
 *
 * @param timestamp a {@link number} with the timestamp
 */
export function getTableDate(timestamp: number) {
  if (timestamp === null) {
    return "-";
  }

  const dateObj = new Date(timestamp);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  let dateString = dateObj.toLocaleDateString(CURRENT_LOCALE, options);
  dateString = capitalize(dateString)?.split(".").join("");
  return dateString;
}

/**
 * Method that returns a localized date for a given {@link number} timestamp
 *
 * @param timestamp a {@link Date} with the wanted Date
 */
export function getTimeLineDate(timestamp: any, useRecruiterTimeZone: boolean = false) {
  const recruiterProfileFeature = getFeatureConfiguration("General Settings", "recruiter-profile");
  if (timestamp === undefined) return "";
  moment.locale(CURRENT_LOCALE);
  const format = recruiterProfileFeature?.config?.timeFormat === "halfDay" ? "ddd, MMM DD, YYYY, hh:mm a" : "ddd, MMM DD, YYYY, HH:mm:ss";
  const dateString = moment(timestamp).tz(moment.tz.guess()).format(format) + ` (${moment.tz.guess()})`;
  return dateString;
}

/**
 * Method that returns a localized date for a given {@link number} timestamp for a custom timezone
 *
 * @param timestamp a {@link Date} with the wanted Date
 */
export function getTimeLineDateCustomTimezone(timestamp: any, timezone: string) {
  const recruiterProfileFeature = getFeatureConfiguration("General Settings", "recruiter-profile");
  if (timestamp === undefined) return "";
  moment.locale(CURRENT_LOCALE);
  const format = recruiterProfileFeature?.config?.timeFormat === "halfDay" ? "ddd, MMM DD, YYYY, hh:mma:ss a" : "ddd, MMM DD, YYYY, HH:mm:ss";
  const dateString = moment(timestamp).tz(timezone).format(format) + ` (${timezone})`;
  return dateString;
}

// Helper function for the getLocalDate method
export const capitalize = (str: string, lower: boolean = false) => {
  const words = (lower ? str?.toLowerCase() : str)?.split(" ");
  let newWords = "";
  words?.forEach((word) => (newWords = newWords + word[0]?.toUpperCase() + word?.slice(1) + " "));
  return newWords.trim();
};

export const toLiteral = (literalObj: LiteralObj) => {
  const id = literalObj?.id ?? "";
  setLiteralsConfiguration(id);
  try {
    // Pick value comming from the database (database return custom tenant literal if exist for the given id, if not translation and if not default en-US)
    const dataBaseDictionary = store?.getState()?.literals?.literals;
    const dataBaseDictionaryWord = dataBaseDictionary?.[id];
    // Pick value from frontend default file
    const frontEndDictionaryWord = _frontEndDictionary?.[id];
    // Select word from database if exist, if not, select from the default frontend file
    const _word = dataBaseDictionaryWord ?? frontEndDictionaryWord;
    // As a developer, I always want to use en-US when navigating the admin panel.    const developerFlag = store.getState()?.tenant?.userProperties?.developerFlag;
    const developerFlag = store.getState()?.tenant?.userProperties?.developerFlag;
    const isAdminPanel = window?.location?.href?.includes("/administrator");
    if (developerFlag && isAdminPanel) return frontEndDictionaryWord ?? id; //

    const literal = _word ?? id ?? "";
    return literal;
  } catch (err) {
    console.error(err);
  }
  return id;
};

function setLiteralsConfiguration(id: string) {
  const currentRouteId = store?.getState()?.navigation?.currentRoute?.id;
  const currentRouteLiterals = store?.getState()?.literalsConfiguration?.configurations?.current_route_literals;
  let newCurrentRouteLiterals: string[] = [id];
  if (Array.isArray(currentRouteLiterals?.[currentRouteId])) newCurrentRouteLiterals = [...currentRouteLiterals?.[currentRouteId], id];
  if (currentRouteId) store.dispatch(_setLiteralsConfiguration({ current_route_literals: { ...currentRouteLiterals, [currentRouteId]: Array.from(new Set(newCurrentRouteLiterals)) } }));
}

export const dayNameLocale = (day: number) => {
  const localeDay = moment()
    ?.locale(CURRENT_LOCALE)
    ?.isoWeekday(day + 1)
    .format("ddd");
  return capitalize(localeDay?.replace(".", ""));
};

export function getTenantLanguagesOptions() {
  const _languages = store?.getState()?.tenant?.selectedTenant?.languages || [];
  const languagesOptions = _languages?.map((language) => ({ id: language, name: getLanguageString(language), image: getLocaleImage(language) }));
  return languagesOptions;
}

export enum CurrencyOptions {
  "Dollars" = "Dollars",
  "Euros" = "Euros",
  "Pounds" = "Pounds",
  "Other" = "Other",
}

export function getCurrencyOptions() {
  const currencies = [CurrencyOptions.Dollars, CurrencyOptions.Euros, CurrencyOptions.Pounds, CurrencyOptions.Other];
  return currencies?.map((denomination, index) => {
    return { order: index, id: denomination, localeName: toLiteral({ id: `administrator.services.process.offerManagement.currency.${denomination}` }), name: denomination };
  });
}

export function getPhaseNameByType(type: number, phase?: ProcessPhase, language?: string) {
  const phaseNameFromLiteral = toLiteral({ id: PhaseTypeName[type] });
  if (phase?.multilanguage) {
    const _language = language || CURRENT_LOCALE;
    const phaseNameKey = `phaseName__${_language}`;
    return phase?.multilanguage?.[phaseNameKey] || phaseNameFromLiteral || phase?.name;
  }
  return phase?.name || phaseNameFromLiteral;
}

export function extractSelectedLanguages(_languages: any, defaultLanguage: string, processLanguages: any[]) {
  // Filter from language array based on existing isoCodes from the multilanguage object
  const filteredLanguages = processLanguages
    .filter((language) => _languages?.includes(language.isoCode))
    .map((language: any) => ({
      ...language,
      default: language.isoCode === defaultLanguage,
    }));

  // Sort the filteredLanguages array so that the default language comes first
  filteredLanguages.sort((a, b) => {
    if (a.isoCode === defaultLanguage) return -1;
    if (b.isoCode === defaultLanguage) return 1;
    return 0;
  });

  return filteredLanguages;
}

export function getTranslation(object: any, key: string, language?: string, subProperty?: string, index?: number) {
  if (object?.multilanguage && language) {
    const languageKey = `${key}__${language}`;
    if (subProperty) {
      if (index !== undefined) {
        return object?.multilanguage[subProperty]?.[index]?.[languageKey] ?? object?.[subProperty]?.[index]?.[key] ?? "";
      } else {
        // there are phases like video where property is inside subProperty but not indexed array like answers.
        return object?.multilanguage[languageKey] ?? object?.[subProperty]?.[key] ?? "";
      }
    } else {
      return object?.multilanguage[languageKey] ?? object?.[key] ?? "";
    }
  } else {
    if (subProperty) {
      // first case is for answers case but in video for example properties are set inside subProperty but not indexed array
      return index !== undefined ? object?.[subProperty]?.[index]?.[key] : object?.[subProperty]?.[key];
    } else {
      return object?.[key] ?? "";
    }
  }
}
export const getDurationLabels = (time: number) => {
  const mintues = time % 60;
  const hours = Math.floor(time / 60);
  const hoursLabel = hours === 1 ? toLiteral({ id: "creator.liveinterview.duration.hour" }) : toLiteral({ id: "creator.liveinterview.duration.hours" });
  const mintuesLabel = toLiteral({ id: "creator.interview.answertime.minutes" });

  if (time < 60) {
    return `${time} ${mintuesLabel}`;
  }
  const hoursPart = `${hours} ${hoursLabel}`;
  const minutesPart = mintues ? `${mintues} ${mintuesLabel}` : "";
  return `${hoursPart} ${minutesPart}`;
};

export const checkEmptyAndNullValues = (value: any) => {
  return isNull(value) || isEmpty(value) ? undefined : value;
};

export const fetchLiteralByPhaseType = async (lang: string, type: number) => {
  try {
    const phaseLiteral = PhaseTypeName[type];
    const filters = {
      language: lang,
      perPage: 1,
      page: 1,
      searchBy: "keySearch",
      literalKeys: [phaseLiteral],
    };
    const literal = await Api.fetchCountryLiterals(filters);
    return literal;
  } catch (e) {
    console.error(e, "Loading literals from FE json");
  }
};

export const getDurationText = (duration: number) => {
  if (!duration) return "";
  if (duration >= 60) {
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    const minutesText = minutes ? ` ${minutes}m` : "";
    return `${hours}h${minutesText}`;
  }
  return `${duration}m`;
};

export const getTime = (timestamp: number | string) => {
  if (!timestamp) return "";
  if (typeof timestamp === "string") {
    const timeParts = timestamp.split(":");
    if (timeParts.length >= 2) {
      return `${timeParts[0]}:${timeParts[1]}`;
    }
    return timestamp;
  }
  return moment(timestamp).format("HH:mm");
};

export function getDuration(seconds: string) {
  const totalDuration = parseInt(seconds);
  const duration = moment.duration(totalDuration, "seconds");
  const hours = duration.hours();
  const minutes = duration.minutes();
  const _seconds = duration.seconds();
  const formatted = hours > 0 ? `${hours}h ${minutes}min ${_seconds}s` : `${minutes}min ${_seconds}s`;
  return formatted;
}
